import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  Send,
  AlertCircle,
  ArrowRight,
  MessageCircle,
} from "lucide-react";

// Public route — no auth. Uses a bare axios instance to avoid the interceptors
// of the admin panel (which redirect to /login on 401).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 20000 });

const YESNO = [
  { value: true, label: "Sí" },
  { value: false, label: "No" },
];

const THEORY_RESULTS = [
  { value: "Apto", label: "Apto" },
  { value: "No apto", label: "No apto" },
  { value: "No me he presentado", label: "No me he presentado" },
];

const PENDING_FEES = [
  { value: "si", label: "Sí" },
  { value: "no", label: "No" },
  { value: "no_lo_se", label: "No lo sé" },
];

function AgentAvatar({ size = 96 }) {
  return (
    <div
      className="shrink-0 rounded-full bg-orange-100 ring-4 ring-orange-200 shadow-sm overflow-hidden flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <img
        src="/agents/kai.png"
        alt="Kai — Asistente Virtual"
        className="w-full h-full object-cover"
        style={{ objectPosition: "50% 20%" }}
      />
    </div>
  );
}

function RadioGroup({ name, value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            data-testid={`${testid}-${String(opt.value)}`}
            onClick={() => onChange(opt.value)}
            className={
              "px-4 h-10 rounded-xl border text-sm font-semibold transition-colors " +
              (active
                ? "bg-orange-500 border-orange-500 text-white shadow-sm"
                : "bg-white border-neutral-200 text-neutral-700 hover:border-orange-300")
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-neutral-800">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {hint && <span className="block text-xs text-neutral-500 mt-0.5">{hint}</span>}
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full h-11 rounded-xl border border-neutral-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";

export default function KaiQuestionnairePage() {
  const { token } = useParams(); // V1.54 · enlace tokenizado de un solo uso
  const [linkStatus, setLinkStatus] = useState(null); // null=cargando | pending | missing | expired | cancelled | completed
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showLockout, setShowLockout] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    transferStudent: null,
    previousDrivingSchool: "",
    theoryApproved: null,
    theoryAttempts: "",
    lastTheoryResult: "",
    pendingTheoryFees: "",
    approvedTheoryAttempt: "",
    practicalAttemptsOtherSchool: "",
    practicalAttemptsNatur: "",
    feesPaid: "",
    notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        if (!token) {
          // V1.54 · Sin token no hay acceso: el enlace permanente fue eliminado.
          setLinkStatus("missing");
          return;
        }
        const { data: link } = await publicApi.get(`/public/kai-questionnaire/${token}`);
        if (link.status !== "pending") {
          setLinkStatus(link.status);
          return;
        }
        const { data } = await publicApi.get("/kai/config");
        setConfig(data);
        if (link.prefill) {
          setForm((f) => ({
            ...f,
            fullName: link.prefill.fullName || "",
            phone: link.prefill.phone || "",
            email: link.prefill.email || "",
          }));
        }
        setLinkStatus("pending");
      } catch (err) {
        setLinkStatus(err?.response?.status === 404 ? "missing" : "error");
      } finally {
        setLoadingConfig(false);
      }
    })();
  }, [token]);

  const update = useCallback((key) => (v) => setForm((f) => ({ ...f, [key]: v })), []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        transferStudent: !!form.transferStudent,
        previousDrivingSchool: form.previousDrivingSchool.trim(),
        theoryApproved: !!form.theoryApproved,
        theoryAttempts: Number(form.theoryAttempts || 0),
        lastTheoryResult: form.lastTheoryResult || "",
        pendingTheoryFees: form.pendingTheoryFees || "",
        approvedTheoryAttempt: Number(form.approvedTheoryAttempt || 0),
        practicalAttemptsOtherSchool: Number(form.practicalAttemptsOtherSchool || 0),
        practicalAttemptsNatur: Number(form.practicalAttemptsNatur || 0),
        feesPaid: Number(form.feesPaid || 0),
        notes: form.notes.trim(),
        token,
      };
      await publicApi.post("/kai/questionnaire", payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el cuestionario.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (linkStatus !== "pending") {
    const msg = linkStatus === "completed"
      ? "Este enlace ya fue utilizado. Cada enlace es de un solo uso."
      : linkStatus === "expired"
        ? "Este enlace ha caducado por seguridad."
        : linkStatus === "cancelled"
          ? "Este enlace ha sido cancelado por la autoescuela."
          : "Enlace no válido. Los enlaces son únicos, personales y con caducidad.";
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-10">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="kai-link-invalid">
          <div className="flex justify-center mb-4"><AgentAvatar size={112} /></div>
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 mb-3">
            <AlertCircle className="h-3.5 w-3.5" /> Acceso no disponible
          </div>
          <h1 className="text-2xl font-bold mb-3">Enlace no disponible</h1>
          <p className="text-neutral-600 leading-relaxed">{msg}</p>
          <p className="text-neutral-500 text-sm mt-3">Pide un enlace nuevo a Natur Autoescuela por WhatsApp.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-10">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="kai-thanks">
          <div className="flex justify-center mb-4"><AgentAvatar size={112} /></div>
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1 mb-3">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Recibido correctamente
          </div>
          <h1 className="text-2xl font-bold mb-3">¡Gracias!</h1>
          <p className="text-neutral-600 whitespace-pre-line leading-relaxed">
            {config?.final_message?.replace(/^✅\s*Gracias\.\s*/, "") || "Tu información ha sido recibida."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 mb-5" data-testid="kai-welcome">
          <div className="flex items-start gap-4">
            <AgentAvatar size={96} />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Natur Autoescuela · {config?.system_mode}</div>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight mt-1">
                {config?.welcome?.title || "Actualización de Expediente"}
              </h1>
            </div>
          </div>
          <p className="text-neutral-700 whitespace-pre-line mt-4 leading-relaxed">
            {config?.welcome?.body}
          </p>
          <button
            type="button"
            onClick={() => setShowLockout((v) => !v)}
            className="mt-4 inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-orange-600 transition-colors"
            data-testid="kai-chat-attempt"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            ¿Puedo hablar con Kai?
          </button>
          {showLockout && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex gap-2" data-testid="kai-lockout">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{config?.conversation_lockout_message}</span>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={submit} className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 space-y-6" data-testid="kai-form">
          {/* --- Datos generales --- */}
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Datos generales</h2>
            <Field label="Nombre y apellidos" required>
              <input type="text" required maxLength={200} value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className={inputCls} data-testid="kai-fullName" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Teléfono" required>
                <input type="tel" required maxLength={40} value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputCls} data-testid="kai-phone" />
              </Field>
              <Field label="Email" hint="Opcional">
                <input type="email" maxLength={200} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls} data-testid="kai-email" />
              </Field>
            </div>
          </section>

          {/* --- Traslado --- */}
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Traslado</h2>
            <Field label="¿Vienes trasladado de otra autoescuela?" required>
              <RadioGroup name="transferStudent" value={form.transferStudent} onChange={update("transferStudent")} options={YESNO} testid="kai-transfer" />
            </Field>
            {form.transferStudent === true && (
              <Field label="¿De qué autoescuela procedes?">
                <input type="text" maxLength={200} value={form.previousDrivingSchool}
                  onChange={(e) => setForm({ ...form, previousDrivingSchool: e.target.value })}
                  className={inputCls} data-testid="kai-previousSchool" />
              </Field>
            )}
          </section>

          {/* --- Examen teórico --- */}
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Examen teórico</h2>
            <Field label="¿Tienes el examen teórico aprobado?" required>
              <RadioGroup name="theoryApproved" value={form.theoryApproved} onChange={update("theoryApproved")} options={YESNO} testid="kai-theoryApproved" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nº de veces que te has presentado al teórico">
                <input type="number" min={0} max={99} value={form.theoryAttempts}
                  onChange={(e) => setForm({ ...form, theoryAttempts: e.target.value })}
                  className={inputCls} data-testid="kai-theoryAttempts" />
              </Field>
              {form.theoryApproved === true && (
                <Field label="¿En qué intento aprobaste?">
                  <input type="number" min={0} max={99} value={form.approvedTheoryAttempt}
                    onChange={(e) => setForm({ ...form, approvedTheoryAttempt: e.target.value })}
                    className={inputCls} data-testid="kai-approvedAttempt" />
                </Field>
              )}
            </div>
            <Field label="Resultado del último examen teórico">
              <RadioGroup name="lastTheoryResult" value={form.lastTheoryResult} onChange={update("lastTheoryResult")} options={THEORY_RESULTS} testid="kai-lastResult" />
            </Field>
            <Field label="¿Tienes pendiente pagar tasas para el teórico?">
              <RadioGroup name="pendingTheoryFees" value={form.pendingTheoryFees} onChange={update("pendingTheoryFees")} options={PENDING_FEES} testid="kai-pendingFees" />
            </Field>
          </section>

          {/* --- Prácticos --- */}
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Exámenes prácticos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="En otra autoescuela">
                <input type="number" min={0} max={99} value={form.practicalAttemptsOtherSchool}
                  onChange={(e) => setForm({ ...form, practicalAttemptsOtherSchool: e.target.value })}
                  className={inputCls} data-testid="kai-practicalOther" />
              </Field>
              <Field label="En Natur Autoescuela">
                <input type="number" min={0} max={99} value={form.practicalAttemptsNatur}
                  onChange={(e) => setForm({ ...form, practicalAttemptsNatur: e.target.value })}
                  className={inputCls} data-testid="kai-practicalNatur" />
              </Field>
            </div>
          </section>

          {/* --- Tasas --- */}
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Tasas</h2>
            <Field label="¿Cuántas tasas DGT 2.1 has pagado hasta la fecha?">
              <input type="number" min={0} max={99} value={form.feesPaid}
                onChange={(e) => setForm({ ...form, feesPaid: e.target.value })}
                className={inputCls} data-testid="kai-feesPaid" />
            </Field>
          </section>

          {/* --- Observaciones --- */}
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Información adicional</h2>
            <Field label="¿Deseas añadir alguna observación?">
              <textarea rows={4} maxLength={2000} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className={"w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"}
                data-testid="kai-notes" />
            </Field>
          </section>

          {/* --- Submit --- */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="kai-submit"
              className="w-full sm:w-auto sm:min-w-[240px] h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 inline-flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Enviar cuestionario
              <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-xs text-neutral-400 mt-3">Tus datos serán tratados por Natur Autoescuela. Modo {config?.system_mode}.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
